import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { RootState, ThunkExtra } from '../store';
import {
  CreateShiftLogResponse,
  ReportTemplate,
  ShiftLog,
  ShiftLogDetails,
  ShiftLogStatusChangeResponse,
  TakeOverDetails,
  UUID,
  Snippet,
  StandingOrderActionResponse,
  TaskActionResponse,
  DateTime,
} from '../../services/dataModels';
import {
  getErrorDetails,
  NetworkErrorDetails,
  ShiftLogDetailError,
} from '../../services/networkUtils';
import { WritableDraft } from 'immer/dist/types/types-external';
import {
  Event,
  LogErrorType,
  trackEvent,
  trackSerializedError,
  trackShiftLogError,
} from '../../services/Analytics';
import {
  selectCurrentAsset,
  selectCurrentSiteCode,
  selectEditedSnippets,
  selectShiftLogDetails,
} from '../selectors';
import { updateRowStatus } from '../helpers';

type ShiftLogsState = {
  shiftLogs: ShiftLog[];
  isLoading: boolean;
  errorMessage?: string | null;
  modal?: boolean;
  logDetailErrorMessage?: string | null;
  logDetailIsLoading: boolean;
  submitTakeOverInProgress: boolean;
  submitTakeOverErrorMessage?: string;
  teamShiftLogs: ShiftLog[];
  reportTemplates?: ReportTemplate[] | null;
  selectedTemplate?: ReportTemplate;
  shiftLogDetails?: ShiftLogDetails | null;
};

const initialState: ShiftLogsState = {
  shiftLogs: [],
  isLoading: false,
  errorMessage: null,
  logDetailErrorMessage: null,
  logDetailIsLoading: false,
  submitTakeOverInProgress: false,
  submitTakeOverErrorMessage: null,
  teamShiftLogs: [],
  reportTemplates: null,
};

interface ShiftLogsArgs {
  siteCode: string;
  assetName: string;
}

const getShiftLogTemplates = createAsyncThunk<
  ReportTemplate[],
  ShiftLogsArgs,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/getShiftLogTemplates',
  async ({ siteCode, assetName }: ShiftLogsArgs, { rejectWithValue, extra: { apiClient } }) => {
    try {
      const response: AxiosResponse<ReportTemplate[]> = await apiClient.getReportTemplates(
        siteCode,
        assetName,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

interface CreateShiftLogArgs {
  siteCode: string;
  assetName: string;
  templateId: string;
}

const createShiftLog = createAsyncThunk<
  CreateShiftLogResponse,
  CreateShiftLogArgs,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/createShiftLog',
  async (
    { siteCode, assetName, templateId }: CreateShiftLogArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<CreateShiftLogResponse> = await apiClient.createShiftLog(
        siteCode,
        assetName,
        templateId,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const updateShiftLog = createAsyncThunk<
  ShiftLogDetails,
  void,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>('shiftLogs/updateShiftLog', async (_, { getState, rejectWithValue, extra: { apiClient } }) => {
  const state = getState();
  const assetName = selectCurrentAsset.raw(state).assetName;
  const siteCode = selectCurrentSiteCode.raw(state);
  const editedSnippet = selectEditedSnippets.raw(state);
  const previShiftLogDetails = selectShiftLogDetails.raw(state);

  if (typeof editedSnippet === 'undefined') {
    return previShiftLogDetails;
  }

  const shiftLogDetails = {
    ...previShiftLogDetails,
    snippets: [editedSnippet],
  };

  try {
    const response: AxiosResponse<ShiftLogDetails> = await apiClient.updateShiftLog(
      siteCode,
      assetName,
      shiftLogDetails,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorDetails(error));
  }
});

interface ShiftLogUpdateStatusArgs {
  siteCode: string;
  shiftSummaryID: UUID;
  takeoverComments?: string;
}

const submitShiftLog = createAsyncThunk<
  ShiftLog,
  void,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>('shiftLogs/submitShiftLog', async (_, { getState, rejectWithValue, extra: { apiClient } }) => {
  try {
    const state = getState();
    const siteCode = selectCurrentSiteCode.raw(state);
    const shiftLogDetails = selectShiftLogDetails.raw(state);

    const response: AxiosResponse<ShiftLog> = await apiClient.submitShiftLog(
      siteCode,
      shiftLogDetails.shiftSummaryID,
    );
    return response.data;
  } catch (error) {
    return rejectWithValue(getErrorDetails(error));
  }
});

interface ShiftLogDetailArgs {
  siteCode: string;
  shiftSummaryID: string;
}

const fetchShiftLogDetails = createAsyncThunk<
  ShiftLogDetails,
  ShiftLogDetailArgs,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'shiftLogs/fetchShiftLogDetails',
  async (
    { siteCode, shiftSummaryID }: ShiftLogDetailArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<ShiftLogDetails> = await apiClient.getShiftLogDetails(
        siteCode,
        shiftSummaryID,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const getShiftLogs = createAsyncThunk<
  ShiftLog[],
  ShiftLogsArgs,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'shiftLogs/getShiftLogs',
  async ({ siteCode, assetName }: ShiftLogsArgs, { rejectWithValue, extra: { apiClient } }) => {
    try {
      const response: AxiosResponse<ShiftLog[]> = await apiClient.getShiftLogs({
        siteCode: siteCode,
        showType: 'current',
        assetName: assetName,
        fileDownload: false,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
  {
    // Skip duplicate in-flight requests.
    condition: (_, { getState }) => !(getState() as RootState).shiftLogs.isLoading,
  },
);
interface GetTeamShiftLogsArgs {
  siteCode: string;
  assetName: string;
}
const getTeamShiftLogs = createAsyncThunk<
  ShiftLog[],
  GetTeamShiftLogsArgs,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'shiftLogs/getTeamShiftLogs',
  async (
    { siteCode, assetName }: GetTeamShiftLogsArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<ShiftLog[]> = await apiClient.getShiftLogs({
        siteCode: siteCode,
        showType: 'all',
        assetName: assetName,
        fileDownload: false,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
  {
    condition: (_, { getState }) => !(getState() as RootState).shiftLogs.isLoading,
  },
);

interface DownloadFileContentArgs {
  siteCode: string;
  shiftSummaryID: UUID;
  lastUpdate: string;
}

const downloadFileContent = createAsyncThunk<
  ShiftLog,
  DownloadFileContentArgs,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'shiftLogs/downloadFileContent',
  async (
    { siteCode, shiftSummaryID }: DownloadFileContentArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<ShiftLog[]> = await apiClient.getShiftLogs({
        siteCode: siteCode,
        shiftSummaryID: shiftSummaryID,
        fileDownload: true,
      });
      return response.data[0];
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

interface GetShiftLogTakeOverDetailsArgs {
  siteCode: string;
  shiftSummaryID: UUID;
}

const getShiftLogTakeOverDetails = createAsyncThunk<
  TakeOverDetails,
  GetShiftLogTakeOverDetailsArgs,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'shiftLogs/getShiftLogTakeOverDetails',
  async (
    { siteCode, shiftSummaryID }: GetShiftLogTakeOverDetailsArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<TakeOverDetails> = await apiClient.getTakeOverDetails(
        siteCode,
        shiftSummaryID,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const submitTakeOver = createAsyncThunk<
  ShiftLogStatusChangeResponse,
  ShiftLogUpdateStatusArgs,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/submitTakeOver',
  async (
    { siteCode, shiftSummaryID, takeoverComments }: ShiftLogUpdateStatusArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    const response: AxiosResponse<ShiftLogStatusChangeResponse> = await apiClient.takeOverShiftLog(
      siteCode,
      shiftSummaryID,
      takeoverComments,
    );

    if (response.data.status === false) {
      return rejectWithValue({
        type: 'takeover_failure',
        message: response.data.message
          ? response.data.message
          : 'Take over failed. Please try again.',
      });
    }

    return response.data;
  },
);

interface StandingOrderActionArgs {
  siteCode?: string;
  assetName?: string;
  displayTemplateId?: UUID;
  standingOrderId: UUID;
  comment?: UUID;
}

const activateStandingOrder = createAsyncThunk<
  StandingOrderActionResponse,
  StandingOrderActionArgs,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/activateStandingOrder',
  async (
    { siteCode, assetName, displayTemplateId, standingOrderId }: StandingOrderActionArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<StandingOrderActionResponse> =
        await apiClient.activateStandingOrder(
          siteCode,
          assetName,
          displayTemplateId,
          standingOrderId,
        );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const completeStandingOrder = createAsyncThunk<
  StandingOrderActionResponse,
  StandingOrderActionArgs,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/completeStandingOrder',
  async (
    { siteCode, assetName, displayTemplateId, standingOrderId }: StandingOrderActionArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<StandingOrderActionResponse> =
        await apiClient.completeStandingOrder(
          siteCode,
          assetName,
          displayTemplateId,
          standingOrderId,
        );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const postStandingOrderComment = createAsyncThunk<
  StandingOrderActionResponse,
  StandingOrderActionArgs,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/postStandingOrderComment',
  async (
    { standingOrderId, comment }: StandingOrderActionArgs,
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    const state = getState();
    const siteCode = selectCurrentSiteCode.raw(state);
    try {
      const response: AxiosResponse<StandingOrderActionResponse> =
        await apiClient.postStandingOrderComment(siteCode, standingOrderId, comment);

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

interface TaskActionArgs {
  displayTemplateId: UUID;
  taskId: UUID;
}

const startTask = createAsyncThunk<
  TaskActionResponse,
  TaskActionArgs & { startTime: DateTime },
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/startTask',
  async (
    { displayTemplateId, taskId, startTime }: TaskActionArgs & { startTime: DateTime },
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    const state = getState();
    const siteCode = selectCurrentSiteCode.raw(state);
    const asset = selectCurrentAsset.raw(state);
    try {
      const response: AxiosResponse<TaskActionResponse> = await apiClient.startTask(
        siteCode,
        asset.assetName,
        displayTemplateId,
        taskId,
        startTime,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const pauseTask = createAsyncThunk<
  TaskActionResponse,
  TaskActionArgs & { comment: string },
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/pauseTask',
  async (
    { displayTemplateId, taskId, comment }: TaskActionArgs & { comment: string },
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    try {
      const state = getState();
      const siteCode = selectCurrentSiteCode.raw(state);
      const asset = selectCurrentAsset.raw(state);
      const response: AxiosResponse<TaskActionResponse> = await apiClient.pauseTask(
        siteCode,
        asset.assetName,
        displayTemplateId,
        taskId,
        comment,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const resumeTask = createAsyncThunk<
  TaskActionResponse,
  TaskActionArgs & { comment: string },
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/resumeTask',
  async (
    { displayTemplateId, taskId, comment }: TaskActionArgs & { comment: string },
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    const state = getState();
    const siteCode = selectCurrentSiteCode.raw(state);
    const asset = selectCurrentAsset.raw(state);
    try {
      const response: AxiosResponse<TaskActionResponse> = await apiClient.resumeTask(
        siteCode,
        asset.assetName,
        displayTemplateId,
        taskId,
        comment,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const completeTask = createAsyncThunk<
  TaskActionResponse,
  TaskActionArgs & { startTime: DateTime; endTime: DateTime },
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/completeTask',
  async (
    {
      displayTemplateId,
      taskId,
      startTime,
      endTime,
    }: TaskActionArgs & { startTime: DateTime; endTime: DateTime },
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    const state = getState();
    const siteCode = selectCurrentSiteCode.raw(state);
    const asset = selectCurrentAsset.raw(state);
    try {
      const response: AxiosResponse<TaskActionResponse> = await apiClient.completeTask(
        siteCode,
        asset.assetName,
        displayTemplateId,
        taskId,
        startTime,
        endTime,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const cancelTask = createAsyncThunk<
  TaskActionResponse,
  TaskActionArgs & { comment: string },
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/cancelTask',
  async (
    { displayTemplateId, taskId, comment }: TaskActionArgs & { comment: string },
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    const state = getState();
    const siteCode = selectCurrentSiteCode.raw(state);
    const asset = selectCurrentAsset.raw(state);
    try {
      const response: AxiosResponse<TaskActionResponse> = await apiClient.cancelTask(
        siteCode,
        asset.assetName,
        displayTemplateId,
        taskId,
        comment,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const updateTaskPercentage = createAsyncThunk<
  TaskActionResponse,
  { taskId: string; percentCompleted: number },
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/updatePercentage',
  async (
    { taskId, percentCompleted }: { taskId: string; percentCompleted: number },
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    const state = getState();
    const { displayTemplateId } = selectEditedSnippets.raw(state);
    const siteCode = selectCurrentSiteCode.raw(state);
    const asset = selectCurrentAsset.raw(state);
    try {
      const response: AxiosResponse<TaskActionResponse> = await apiClient.updatePercentage(
        siteCode,
        displayTemplateId,
        asset.assetName,
        taskId,
        percentCompleted,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

interface TaskCommentArgs {
  taskId: UUID;
  comment: string;
}

const postTaskComment = createAsyncThunk<
  TaskActionResponse,
  TaskCommentArgs,
  { state: RootState; rejectValue: ShiftLogDetailError; extra: ThunkExtra }
>(
  'shiftLogs/postTaskComment',
  async (
    { taskId, comment }: TaskCommentArgs,
    { rejectWithValue, getState, extra: { apiClient } },
  ) => {
    const state = getState();
    const siteCode = selectCurrentSiteCode.raw(state);
    const asset = selectCurrentAsset.raw(state);
    try {
      const response: AxiosResponse<TaskActionResponse> = await apiClient.postTaskComment(
        siteCode,
        asset.assetName,
        taskId,
        comment,
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const unsubmitShiftLog = createAsyncThunk<
  ShiftLog,
  ShiftLogUpdateStatusArgs,
  { state: RootState; rejectValue: NetworkErrorDetails; extra: ThunkExtra }
>(
  'shiftLogs/unsubmitShiftLog',
  async (
    { siteCode, shiftSummaryID }: ShiftLogUpdateStatusArgs,
    { rejectWithValue, extra: { apiClient } },
  ) => {
    try {
      const response: AxiosResponse<ShiftLog> = await apiClient.unsubmitShiftLog(
        siteCode,
        shiftSummaryID,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorDetails(error));
    }
  },
);

const shiftLogSlice = createSlice({
  name: 'shiftLogs',
  initialState: initialState,
  reducers: {
    setComments(state, action: PayloadAction<{ comments: string; snippetName: string }>) {
      if (!state.shiftLogDetails) {
        return;
      }

      const { snippetName, comments } = action.payload;
      updateComments(state.shiftLogDetails.snippets, snippetName, comments);
      state.shiftLogDetails.snippets
        .filter((s) => s.type === 'snippetGroup')
        .map((group) => {
          updateComments(group.groupSnippets, snippetName, comments);
        });
    },
    modalChanged(state, { payload }: PayloadAction<boolean>) {
      if (payload) {
        trackEvent(Event.OpenedTemplateSelectionModal);
      }

      state.modal = payload;
    },
    templateSelected(state, { payload }: PayloadAction<ReportTemplate | undefined>) {
      state.selectedTemplate = payload;
    },
    resetCreateShiftLog(state) {
      state.selectedTemplate = undefined;
    },
    resetErrorMessage(state) {
      state.errorMessage = undefined;
    },
    resetShiftLogDetailErrorMessage(state) {
      state.logDetailErrorMessage = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getShiftLogTemplates.fulfilled, (state, { payload }) => {
        state.reportTemplates = payload;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(getShiftLogTemplates.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
        state.reportTemplates = [];
      })
      .addCase(getShiftLogTemplates.rejected, (state, { payload: networkError }) => {
        state.reportTemplates = [];
        state.isLoading = false;

        const { type, message } = networkError;
        if (type === 'server_error' && message) {
          state.errorMessage = message;
        } else {
          state.errorMessage = 'Something went wrong. Please try again.';
        }
        trackShiftLogError(networkError, LogErrorType.RefreshTemplates);
      });

    builder
      .addCase(createShiftLog.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.selectedTemplate = undefined;
        const { snippets, ...shiftLog } = payload;

        state.shiftLogs = [...state.shiftLogs, shiftLog];
        state.shiftLogDetails = { shiftSummaryID: shiftLog.shiftSummaryID, snippets };
      })
      .addCase(createShiftLog.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(createShiftLog.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;
        const { type, message } = networkError;
        if (type === 'client_error' && message) {
          state.errorMessage = message;
        } else {
          state.errorMessage = 'Something went wrong. Please try again.';
        }
        trackShiftLogError(networkError, LogErrorType.CreateShiftLog);
      });
    builder
      .addCase(updateShiftLog.fulfilled, (state, { payload }) => {
        state.logDetailIsLoading = false;
        state.shiftLogDetails = payload;
        trackEvent(Event.UpdateShiftLog);
      })
      .addCase(updateShiftLog.pending, (state) => {
        state.logDetailIsLoading = true;
        state.logDetailErrorMessage = null;
      })
      .addCase(updateShiftLog.rejected, (state, { payload: networkError }) => {
        state.logDetailIsLoading = false;
        state.logDetailErrorMessage = networkError && networkError.message
          ? networkError.message
          : 'Unable to update shift log at the moment.';
        trackSerializedError(networkError, LogErrorType.UpdateLog);
      });

    builder
      .addCase(fetchShiftLogDetails.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.logDetailIsLoading = false;
        state.errorMessage = null;
        state.logDetailErrorMessage = undefined;
        state.shiftLogDetails = payload;
      })
      .addCase(fetchShiftLogDetails.pending, (state) => {
        state.isLoading = true;
        state.logDetailIsLoading = true;
        state.errorMessage = null;
        state.logDetailErrorMessage = undefined;
      })
      .addCase(fetchShiftLogDetails.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;
        state.logDetailIsLoading = false;
        state.errorMessage = networkError && networkError.message
          ? networkError.message
          : 'Unable to get shift log details.';
        trackSerializedError(networkError, LogErrorType.FetchShiftLogDetail);
      });

    builder
      .addCase(getShiftLogs.fulfilled, (state, { payload }) => {
        state.shiftLogs = payload;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(getShiftLogs.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(getShiftLogs.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;
        state.errorMessage = networkError && networkError.message
          ? networkError.message
          : 'Unable to refresh shift logs.';
        trackSerializedError(networkError, LogErrorType.RefreshShiftLogs);
      });

    builder
      .addCase(getTeamShiftLogs.fulfilled, (state, { payload }) => {
        state.teamShiftLogs = payload;
        state.isLoading = false;
        state.errorMessage = null;
      })
      .addCase(getTeamShiftLogs.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = null;
      })
      .addCase(getTeamShiftLogs.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;
        state.errorMessage = networkError && networkError.message
          ? networkError.message
          : 'Unable to refresh team shift logs.';
        trackSerializedError(networkError, LogErrorType.RefreshShiftLogs);
      });

    builder
      .addCase(downloadFileContent.fulfilled, (state, { payload }) => {
        const response = payload;
        const shiftLogIndex = state.shiftLogs.findIndex(
          (shiftLog) => shiftLog.shiftSummaryID === response.shiftSummaryID,
        );
        // Insert when the log isn't already in the list (e.g. deep-linked details) so
        // its downloaded fileContent is reachable; otherwise state.shiftLogs[-1] is lost.
        if (shiftLogIndex >= 0) {
          state.shiftLogs[shiftLogIndex] = response;
        } else {
          state.shiftLogs.push(response);
        }
        state.errorMessage = null;
      })
      .addCase(downloadFileContent.pending, (state) => {
        state.errorMessage = null;
      })
      .addCase(downloadFileContent.rejected, (state, { payload: networkError }) => {
        state.errorMessage = networkError && networkError.message
          ? networkError.message
          : 'Unable to download shift log details.';
        trackSerializedError(networkError, LogErrorType.DonwloadReportLog);
      });

    builder
      .addCase(submitShiftLog.fulfilled, (state) => {
        state.logDetailErrorMessage = null;
        state.logDetailIsLoading = false;
        trackEvent(Event.SubmitShiftLog);
      })
      .addCase(submitShiftLog.pending, (state) => {
        state.logDetailErrorMessage = null;
        state.logDetailIsLoading = true;
      })
      .addCase(submitShiftLog.rejected, (state, { payload: networkError }) => {
        state.logDetailIsLoading = false;
        state.logDetailErrorMessage = networkError && networkError.message
          ? networkError.message
          : 'Shift log could not be submitted at this time.';
        trackShiftLogError(networkError, LogErrorType.SubmitLog);
      });

    builder
      .addCase(unsubmitShiftLog.fulfilled, (state) => {
        //need to implement what happens when successful
        state.errorMessage = null;
        state.isLoading = false;
      })
      .addCase(unsubmitShiftLog.pending, (state) => {
        state.errorMessage = null;
        state.isLoading = true;
      })
      .addCase(unsubmitShiftLog.rejected, (state, { payload: networkError }) => {
        state.isLoading = false;
        state.errorMessage = networkError && networkError.message
          ? networkError.message
          : 'Unable to un-submit the shift log.';
        trackSerializedError(networkError, LogErrorType.UnsubmitLog);
      });

    builder
      .addCase(submitTakeOver.fulfilled, (state) => {
        state.submitTakeOverInProgress = false;
      })
      .addCase(submitTakeOver.pending, (state) => {
        state.submitTakeOverErrorMessage = null;
        state.submitTakeOverInProgress = true;
      })
      .addCase(submitTakeOver.rejected, (state, { payload: networkError }) => {
        state.submitTakeOverInProgress = false;
        state.submitTakeOverErrorMessage = networkError && networkError.message
          ? networkError.message
          : 'Take over failed, please try again later.';
        trackShiftLogError(networkError, LogErrorType.TakeOverLog);
      });

      builder
      .addCase(getShiftLogTakeOverDetails.fulfilled, (state) => {
        state.submitTakeOverInProgress = false;
      })
      .addCase(getShiftLogTakeOverDetails.pending, (state) => {
        state.submitTakeOverErrorMessage = null;
        state.submitTakeOverInProgress = true;
      })
      .addCase(getShiftLogTakeOverDetails.rejected, (state, { payload: networkError }) => {
        state.submitTakeOverInProgress = false;
        state.submitTakeOverErrorMessage = networkError && networkError.message
          ? networkError.message
          : 'Fetching take over details failed, please try again later.';
        trackShiftLogError(networkError, LogErrorType.FetchTakeOverDetails);
      });

    [activateStandingOrder, completeStandingOrder, postStandingOrderComment].forEach((action) => {
      builder
        .addCase(action.fulfilled, (state, { payload }) => {
          state.logDetailIsLoading = false;

          if (action === activateStandingOrder || action === completeStandingOrder) {
            state.shiftLogDetails.snippets.forEach((snippet) => {
              updateRowStatus(
                snippet,
                'StandingOrder',
                { key: 'standingOrderId', value: payload.standingOrderId },
                { key: 'statusDisplayName', value: payload.statusDisplayName },
              );
            });
          }

          switch (action) {
            case activateStandingOrder:
              trackEvent(Event.ActivateStandingOrder);
              break;
            case completeStandingOrder:
              trackEvent(Event.CompleteStandingOrder);
              break;
            default:
              break;
          }
        })
        .addCase(action.pending, (state) => {
          state.logDetailIsLoading = true;
          state.logDetailErrorMessage = null;
        })
        .addCase(action.rejected, (state, { payload: networkError }) => {
          state.logDetailIsLoading = false;
          let defaultErrorMessage = '';

          switch (action) {
            case activateStandingOrder:
              defaultErrorMessage = 'Unable to activate the standing order.';
              trackSerializedError(networkError, LogErrorType.CompleteStandingOrder);
              break;
            case completeStandingOrder:
              defaultErrorMessage = 'Unable to complete the standing order.';
              trackSerializedError(networkError, LogErrorType.CompleteStandingOrder);
              break;
            case postStandingOrderComment:
              defaultErrorMessage = 'Unable to post standing order comment.';
              trackSerializedError(networkError, LogErrorType.CompleteStandingOrder);
              break;
            default:
              break;
          }

          state.logDetailErrorMessage = networkError && networkError.message
            ? networkError.message
            : defaultErrorMessage;
        });
    });

    [
      startTask,
      pauseTask,
      resumeTask,
      cancelTask,
      completeTask,
      updateTaskPercentage,
      postTaskComment,
    ].forEach((action) => {
      builder
        .addCase(action.fulfilled, (state, { payload }) => {
          state.logDetailIsLoading = false;
          state.shiftLogDetails.snippets.forEach((snippet) => {
            if (snippet.type !== 'Task') return;

            const i = snippet.data[0].rowData.findIndex(
              (rowData) => rowData.taskId === payload.taskId,
            );

            if (i < 0) return;

            snippet.data[0].rowData[i] = {
              ...payload,
            };
          });

          switch (action) {
            case startTask:
              trackEvent(Event.StartTask);
              break;
            case pauseTask:
              trackEvent(Event.PauseTask);
              break;
            case resumeTask:
              trackEvent(Event.ResumeTask);
              break;
            case cancelTask:
              trackEvent(Event.CancelTask);
              break;
            case completeTask:
              trackEvent(Event.CompleteTask);
              break;
            case updateTaskPercentage:
              trackEvent(Event.UpdateTaskCompletionPercentage);
              break;
            case postTaskComment:
              trackEvent(Event.PostTaskComment);
              break;
            default:
              break;
          }
          trackEvent(Event.StartTask);
        })
        .addCase(action.pending, (state) => {
          state.logDetailIsLoading = true;
          state.logDetailErrorMessage = null;
        })
        .addCase(action.rejected, (state, { payload: networkError }) => {
          state.logDetailIsLoading = false;
          let defaultErrorMessage = '';
          switch (action) {
            case startTask:
              defaultErrorMessage = 'Unable to start the task.';
              trackSerializedError(networkError, LogErrorType.StartTask);
              break;
            case pauseTask:
              defaultErrorMessage = 'Unable to pause the task.';
              trackSerializedError(networkError, LogErrorType.PauseTask);
              break;
            case resumeTask:
              defaultErrorMessage = 'Unable to resume the task.';
              trackSerializedError(networkError, LogErrorType.ResumeTask);
              break;
            case cancelTask:
              defaultErrorMessage = 'Unable to cancel the task.';
              trackSerializedError(networkError, LogErrorType.CancelTask);
              break;
            case completeTask:
              defaultErrorMessage = 'Unable to complete the task.';
              trackSerializedError(networkError, LogErrorType.CompleteTask);
              break;
            case updateTaskPercentage:
              defaultErrorMessage = 'Unable to update task completion percentage.';
              trackSerializedError(networkError, LogErrorType.UpdateTaskCompletionPercentage);
              break;
            case postTaskComment:
              defaultErrorMessage = 'Unable to post task comment.';
              trackSerializedError(networkError, LogErrorType.PostTaskComment);
              break;
            default:
              break;
          }
          state.logDetailErrorMessage = networkError && networkError.message
            ? networkError.message
            : defaultErrorMessage;
        });
    });
  },
});

const updateComments = (
  snippets: WritableDraft<Snippet>[],
  snippetName: string,
  comments: string,
) => {
  const commentSnippet = snippets.find((s) => s.type === 'Comment' && s.name === snippetName);
  if (commentSnippet) {
    commentSnippet.data[0].rowData[0].Note = comments;
  }
};

export default shiftLogSlice.reducer;

export const actions = {
  ...shiftLogSlice.actions,
  getShiftLogTemplates,
  createShiftLog,
  updateShiftLog,
  fetchShiftLogDetails,
  getShiftLogs,
  getTeamShiftLogs,
  downloadFileContent,
  getShiftLogTakeOverDetails,
  submitTakeOver,
  submitShiftLog,
  unsubmitShiftLog,
  shiftLogSlice,
  activateStandingOrder,
  postStandingOrderComment,
  completeStandingOrder,
  startTask,
  pauseTask,
  resumeTask,
  completeTask,
  cancelTask,
  updateTaskPercentage,
  postTaskComment,
};
